const RagGdrivePinecone = (props) => {
    return (
        <>
            <h1 className="d-title">n8n Google Drive to Pinecone RAG Sync Engine</h1>
            <hr />
            <p className="mt-4">
                This workflow updates various company documents into a searchable AI knowledge base (Vector Database) in near real-time.
            </p>
            <h3 className="mt-4">🔴 THE PROBLEM</h3>
            <p>
                For companies using cloud storage like Google Drive to store important documents (policies, SOPs, HR guides), employees often waste valuable time manually searching through folders for specific information. This inefficiency creates productivity bottlenecks and increases the risk of using outdated or incorrect data.
            </p>
            <h3 className="mt-4">🟢 THE SOLUTION</h3>
            <p>
                To solve this friction, I built an automated, event-driven pipeline using n8n that completely removes the manual search process:
            </p>
            <ul>
                <li><strong>Automated Event Listening:</strong> n8n continuously monitors a targeted Google Drive folder for new file uploads, updates, or deletions.</li>
                <li><strong>Document Processing:</strong> Automatically extracts and parses text from the incoming documents.</li>
                <li><strong>Intelligent Chunking & Embedding:</strong> Splits the text into semantic chunks and passes them through an embedding model.</li>
                <li><strong>Vector DB Synchronization:</strong> Upserts the new embeddings into the vector database. When a file drops into the folder, the AI learns it instantly. When a file is deleted, the AI forgets it.</li>
            </ul>
            <h3 className="mt-4">📈 RESULTS & BUSINESS IMPACT</h3>
            <p>
                By automating this knowledge sync, the pipeline transforms a static file drive into an instant conversational oracle. Based on standard enterprise workflows, implementing this yields significant ROI:
            </p>
            <ul>
                <li><strong>99% Faster Information Retrieval:</strong> Reduces the average time an employee spends searching for internal guidelines from ~15 minutes per query to under 5 seconds.</li>
                <li><strong>Massive Time Savings:</strong> In a 500-employee company where staff search for internal info just twice a week, this reclaims an estimated 1,000+ productive hours per month previously lost to manual searching.</li>
                <li><strong>~30% Faster Onboarding:</strong> New hires can instantly ask the AI, "What is our remote work policy?" or "How do I request software access?"—deflecting hundreds of routine questions away from HR and IT.</li>
                <li><strong>Zero Stale Data Risk:</strong> Because the database syncs the moment a document is revised in Drive, compliance and legal teams can be confident employees are always querying the exact, up-to-date guidelines.</li>
            </ul>
            <h3 className="mt-4">🚀FUTURE ENHANCEMENT</h3>
            <p>
                For further improvements, these are some possible steps to enhance the workflow:
            </p>
            <ul>
                <li>Broaden File Type Support: The current workflow strictly supports 4 core file types. Future iterations will expand parser capabilities to handle a much wider array of enterprise file formats.</li>
                <li>Add OCR (Optical Character Recognition) capabilities to extract text from scanned compliance PDFs and images.</li>
                <li>Expand the n8n workflow to aggregate data from multiple departmental silos simultaneously (e.g., Notion for Product, Slack for Engineering, Drive for HR).</li>
            </ul>
            <p className="mt-4">
                This architecture solves the true bottleneck of enterprise AI: data freshness. By keeping the AI's knowledge base continuously and autonomously updated, it transforms a standard LLM into an invaluable, self-sustaining corporate asset.
            </p>
            <h3>Simplified Architecture Diagram</h3>
            <img
                src="img/ragGdrivePinecone/simplified_diagram.svg"
                alt="Simplified Architecture Diagram"
                style={{ objectFit: 'contain', maxWidth: '100%' }}
                className="rounded mx-auto d-block w-50 gambar_alur mb-4 mt-4"
            />
            <p>
                The architecture is designed to be straightforward and reliable, ensuring data flows seamlessly from Google Drive to Pinecone.
            </p>

            <h3>Actual n8n Workflow Diagram</h3>
            <img
                src="img/ragGdrivePinecone/actual_diagram.png"
                alt="Actual n8n Workflow Diagram"
                style={{ objectFit: 'contain', maxWidth: '100%' }}
                className="rounded mx-auto d-block w-100 gambar_alur mb-4 mt-4"
            />
            <p>
                The n8n workflow manages the entire process from fetching files, extracting text, generating embeddings, to finally upserting the data into Pinecone.
            </p>
            <p className="mt-4">
                <strong>Github:</strong> <a href="https://github.com/giovanniabel/n8n-gdrive-rag-sync" target="_blank" rel="noreferrer">n8n-gdrive-rag-sync</a>
            </p>
            <p className="mt-4">
                <strong>n8n Workflow:</strong> <a href="https://n8n.io/workflows/16625-sync-google-drive-documents-to-pinecone-rag-with-google-gemini-embeddings/" target="_blank" rel="noreferrer">Sync Google Drive Documents to Pinecone (RAG)</a>
            </p>
        </>
    )
}

export default RagGdrivePinecone;
